import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { api_url } from '../config';
import UpdatePassword from './UpdatePassword';
import UpdateCardDetails from './UpdateCardDetails';
import SkeletonLoader from './AccountDetails/SkeletonLoader';
import ProfileView from './AccountDetails/ProfileView';
import ProfileForm from './AccountDetails/ProfileForm';

const AccountDetails = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [userData, setUserData] = useState([]);
    const [UpdatePasswordModal, setUpdatePasswordModal] = useState(false);
    const [UpdateCardModal, setUpdateCardModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [editingData, setEditingData] = useState({});

    const { id, token } = useSelector((state) => state.auth);
    
    const fetchUserProfile = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(api_url + `/v1/user/${id}/profile`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            const data = response.data.data;
            setUserData(data);
        } catch (error) {
            toast.error("Failed to load profile data");
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]);

    useEffect(() => {
        setEditingData({...userData});
    }, [userData]);

    const handleEdit = () => {
        setEditingData({ ...userData });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEditingData({ ...userData });
        setIsEditing(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditingData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
        
        // Clear error when field is filled
        if (value.trim() !== '' && errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            { name: 'name', label: 'Admin Name' },
            { name: 'email', label: 'Email' },
            { name: 'company_name', label: 'Company Name' },
            { name: 'company_phone', label: 'Company Mobile Number' },
            { name: 'company_type', label: 'Company Type' },
            { name: 'address', label: 'Company Address' },
            { name: 'country', label: 'Country' },
            { name: 'city', label: 'City' }
        ];

        requiredFields.forEach(field => {
            if (!editingData[field.name] || editingData[field.name].trim() === '') {
                newErrors[field.name] = `${field.label} is required`;
            }
        });

        // Email validation
        if (editingData.email && !/\S+@\S+\.\S+/.test(editingData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        
        // Validate form first
        if (!validateForm()) {
            toast.error('Please fill in all required fields correctly');
            return;
        }
        
        setLoading(true);

        // Create a copy of the data to send
        const dataToSend = { ...editingData };
        
        // Ensure company_website is properly formatted
        if (dataToSend.company_website && !dataToSend.company_website.startsWith('http')) {
            if (dataToSend.company_website.trim() !== '') {
                dataToSend.company_website = 'https://' + dataToSend.company_website.replace(/^[/\\]+/, '');
            }
        }

        try {
            const response = await api.put(api_url + `/v1/user/${id}/profile`, dataToSend, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            
            setUserData(response.data.data);
            setIsEditing(false);
            toast.success('Update successful');
        } catch (error) {
            if (error.response) {
                if (error.response.data && error.response.data.error === 'At least one value must change') {
                    toast.info('No changes were detected. Please modify at least one field.');
                } 
                else if (error.response.data && error.response.data.errors) {
                    const errorMessages = Object.entries(error.response.data.errors)
                        .map(([field, messages]) => `${field}: ${messages.join(', ')}`);
                    errorMessages.forEach(msg => toast.error(msg));
                } else if (error.response.data && error.response.data.message) {
                    toast.error(error.response.data.message);
                } else {
                    toast.error('Validation error. Please check your form inputs.');
                }
            } else {
                toast.error('Failed to update user data. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className=''>
            {loading ? (
                <SkeletonLoader />
            ) : isEditing ? (
                <ProfileForm 
                    editingData={editingData}
                    userData={userData}
                    errors={errors}
                    loading={loading}
                    handleInputChange={handleInputChange}
                    handleCancel={handleCancel}
                    handleUpdate={handleUpdate}
                    setUpdatePasswordModal={setUpdatePasswordModal}
                    setUpdateCardModal={setUpdateCardModal}
                />
            ) : (
                <ProfileView 
                    userData={userData} 
                    onEdit={handleEdit} 
                />
            )}

            {UpdatePasswordModal && (
                <div className="fixed inset-0 flex items-center justify-center w-full h-full bg-gray-200 bg-opacity-70 backdrop-blur-sm">
                    <UpdatePassword toggle={() => { setUpdatePasswordModal(false) }} />
                </div>
            )}
            
            {UpdateCardModal && (
                <div className="fixed inset-0 flex items-center justify-center w-full h-full bg-gray-200 bg-opacity-70 backdrop-blur-sm">
                    <UpdateCardDetails 
                        toggle={() => { setUpdateCardModal(false) }} 
                        onSuccess={fetchUserProfile}
                    />
                </div>
            )}
        </div>
    );
};

export default AccountDetails;